import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  AlertCircle, AlertTriangle, CheckCircle2, Eye, EyeOff, Loader2, Plus, Siren,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import {
  INCIDENT_SEVERITIES,
  INCIDENT_STATES,
  createIncident,
  getIncident,
  listIncidents,
  listStatusComponents,
  postIncidentUpdate,
  updateIncidentMeta,
  type IncidentSeverity,
  type IncidentState,
} from "@/lib/incidents.functions";

export const Route = createFileRoute("/_authenticated/app/settings/incidents")({
  head: () => ({
    meta: [
      { title: "Incident console — CairnOS" },
      {
        name: "description",
        content: "Declare incidents, post updates, and resolve operational issues across the platform.",
      },
    ],
  }),
  component: IncidentsPage,
});

function severityTone(s: IncidentSeverity) {
  return s === "critical"
    ? "bg-destructive/10 text-destructive border-destructive/30"
    : s === "major"
      ? "bg-warning/15 text-warning border-warning/30"
      : "bg-info/10 text-info border-info/30";
}

function stateTone(s: IncidentState) {
  if (s === "resolved") return "bg-success/15 text-success";
  if (s === "monitoring") return "bg-info/15 text-info";
  if (s === "identified") return "bg-warning/15 text-warning";
  return "bg-destructive/15 text-destructive";
}

function StateIcon({ state }: { state: IncidentState }) {
  if (state === "resolved") return <CheckCircle2 className="h-3.5 w-3.5" />;
  if (state === "monitoring") return <AlertCircle className="h-3.5 w-3.5" />;
  if (state === "identified") return <AlertTriangle className="h-3.5 w-3.5" />;
  return <Siren className="h-3.5 w-3.5" />;
}

function IncidentsPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const listFn = useServerFn(listIncidents);
  const compFn = useServerFn(listStatusComponents);
  const createFn = useServerFn(createIncident);
  const updateMetaFn = useServerFn(updateIncidentMeta);

  const [filter, setFilter] = useState<"open" | "all">("open");
  const [openCreate, setOpenCreate] = useState(false);
  const [inspectId, setInspectId] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ["incidents", tenantId, filter],
    enabled: !!tenantId,
    queryFn: () =>
      listFn({
        data: {
          tenantId: tenantId!,
          includeResolved: filter === "all",
          limit: 100,
        },
      }),
    refetchInterval: 30_000,
  });

  const componentsQ = useQuery({
    queryKey: ["status-components", tenantId],
    enabled: !!tenantId,
    queryFn: () => compFn({ data: { tenantId: tenantId! } }),
  });

  const rows = listQ.data ?? [];
  const components = componentsQ.data ?? [];

  const stats = useMemo(() => {
    const open = rows.filter((r: any) => r.state !== "resolved").length;
    const critical = rows.filter((r: any) => r.severity === "critical" && r.state !== "resolved").length;
    const last24 = rows.filter(
      (r: any) => new Date(r.started_at).getTime() > Date.now() - 86_400_000,
    ).length;
    return { open, critical, last24, total: rows.length };
  }, [rows]);

  // Create dialog state
  const [form, setForm] = useState({
    title: "",
    severity: "minor" as IncidentSeverity,
    customerVisible: true,
    affectedComponents: [] as string[],
    initialMessage: "",
  });

  const openNew = () => {
    setForm({
      title: "",
      severity: "minor",
      customerVisible: true,
      affectedComponents: [],
      initialMessage: "",
    });
    setOpenCreate(true);
  };

  const createMut = useMutation({
    mutationFn: () =>
      createFn({
        data: {
          tenantId: tenantId!,
          title: form.title.trim(),
          severity: form.severity,
          customerVisible: form.customerVisible,
          affectedComponents: form.affectedComponents,
          initialMessage: form.initialMessage.trim(),
        },
      }),
    onSuccess: (res) => {
      toast.success("Incident declared");
      setOpenCreate(false);
      qc.invalidateQueries({ queryKey: ["incidents", tenantId] });
      setInspectId(res.id);
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to declare"),
  });

  const toggleVisibility = useMutation({
    mutationFn: (v: { id: string; visible: boolean }) =>
      updateMetaFn({ data: { tenantId: tenantId!, id: v.id, customerVisible: v.visible } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["incidents", tenantId] }),
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Siren className="h-6 w-6" /> Incident console
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Declare incidents, post operator updates, and flip components on your public status page.
          </p>
        </div>
        <Button onClick={openNew} variant="destructive">
          <Plus className="h-4 w-4 mr-2" /> Declare incident
        </Button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Open" value={stats.open} tone={stats.open > 0 ? "warn" : "default"} />
        <StatCard label="Critical open" value={stats.critical} tone={stats.critical > 0 ? "alert" : "default"} />
        <StatCard label="Last 24h" value={stats.last24} />
        <StatCard label="Total tracked" value={stats.total} />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Incidents</CardTitle>
            <CardDescription>
              {filter === "open" ? "Showing open incidents only." : "Showing all incidents."}
            </CardDescription>
          </div>
          <Select value={filter} onValueChange={(v) => setFilter(v as any)}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open only</SelectItem>
              <SelectItem value="all">All incidents</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {listQ.isLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              {filter === "open" ? "No open incidents. Quiet day." : "No incidents recorded yet."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((i: any) => (
                  <TableRow key={i.id}>
                    <TableCell>
                      <button
                        className="font-medium hover:underline text-left"
                        onClick={() => setInspectId(i.id)}
                      >
                        {i.title}
                      </button>
                      {i.affected_components?.length > 0 && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {i.affected_components.join(", ")}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={severityTone(i.severity)}>{i.severity}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={stateTone(i.state)}>
                        <span className="inline-flex items-center gap-1">
                          <StateIcon state={i.state} /> {i.state}
                        </span>
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDistanceToNow(new Date(i.started_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell>
                      <button
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => toggleVisibility.mutate({ id: i.id, visible: !i.customer_visible })}
                        title={i.customer_visible ? "Public on status page" : "Internal only"}
                      >
                        {i.customer_visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                        {i.customer_visible ? "Public" : "Internal"}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setInspectId(i.id)}>Open</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Declare incident</DialogTitle>
            <DialogDescription>
              Open an investigating incident and post the first update teammates (and optionally
              customers) will see.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Carrier label generation delayed"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v as IncidentSeverity }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INCIDENT_SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Affected components</Label>
                <Select
                  value=""
                  onValueChange={(v) => {
                    if (!v) return;
                    setForm((f) =>
                      f.affectedComponents.includes(v)
                        ? f
                        : { ...f, affectedComponents: [...f.affectedComponents, v] },
                    );
                  }}
                >
                  <SelectTrigger><SelectValue placeholder={`${form.affectedComponents.length} selected`} /></SelectTrigger>
                  <SelectContent>
                    {components.length === 0 ? (
                      <div className="px-2 py-3 text-xs text-muted-foreground">
                        No components configured.
                      </div>
                    ) : (
                      components.map((c: any) => (
                        <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {form.affectedComponents.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {form.affectedComponents.map((slug) => (
                      <Badge
                        key={slug}
                        variant="outline"
                        className="cursor-pointer"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            affectedComponents: f.affectedComponents.filter((x) => x !== slug),
                          }))
                        }
                      >
                        {slug} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Initial update</Label>
              <Textarea
                rows={3}
                value={form.initialMessage}
                onChange={(e) => setForm((f) => ({ ...f, initialMessage: e.target.value }))}
                placeholder="We're investigating elevated error rates from our label-buy provider…"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">Show on public status page</div>
                <div className="text-xs text-muted-foreground">
                  Disable for internal-only incidents (no customer impact).
                </div>
              </div>
              <Switch
                checked={form.customerVisible}
                onCheckedChange={(v) => setForm((f) => ({ ...f, customerVisible: v }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => createMut.mutate()}
              disabled={
                createMut.isPending ||
                form.title.trim().length < 3 ||
                form.initialMessage.trim().length < 1
              }
            >
              {createMut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Declare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inspect sheet */}
      <IncidentSheet
        id={inspectId}
        onOpenChange={(o) => !o && setInspectId(null)}
        tenantId={tenantId ?? null}
      />
    </div>
  );
}

function IncidentSheet({
  id,
  tenantId,
  onOpenChange,
}: {
  id: string | null;
  tenantId: string | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const getFn = useServerFn(getIncident);
  const postFn = useServerFn(postIncidentUpdate);

  const q = useQuery({
    queryKey: ["incident", id],
    enabled: !!id && !!tenantId,
    queryFn: () => getFn({ data: { tenantId: tenantId!, id: id! } }),
  });

  const [state, setState] = useState<IncidentState>("identified");
  const [message, setMessage] = useState("");

  const post = useMutation({
    mutationFn: () =>
      postFn({
        data: { tenantId: tenantId!, incidentId: id!, state, message: message.trim() },
      }),
    onSuccess: () => {
      toast.success("Update posted");
      setMessage("");
      qc.invalidateQueries({ queryKey: ["incident", id] });
      qc.invalidateQueries({ queryKey: ["incidents", tenantId] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to post"),
  });

  const incident = q.data?.incident as any;
  const updates = q.data?.updates ?? [];

  return (
    <Sheet open={!!id} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{incident?.title ?? "Incident"}</SheetTitle>
          <SheetDescription>
            {incident && (
              <span className="inline-flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={severityTone(incident.severity)}>{incident.severity}</Badge>
                <Badge variant="outline" className={stateTone(incident.state)}>
                  <span className="inline-flex items-center gap-1">
                    <StateIcon state={incident.state} /> {incident.state}
                  </span>
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Started {format(new Date(incident.started_at), "MMM d, HH:mm")}
                </span>
                {incident.resolved_at && (
                  <span className="text-xs text-muted-foreground">
                    · Resolved {format(new Date(incident.resolved_at), "MMM d, HH:mm")}
                  </span>
                )}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        {q.isLoading || !incident ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            {incident.affected_components?.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                  Affected components
                </div>
                <div className="flex flex-wrap gap-1">
                  {incident.affected_components.map((c: string) => (
                    <Badge key={c} variant="outline">{c}</Badge>
                  ))}
                </div>
              </div>
            )}

            {incident.state !== "resolved" && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Post Update</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label>New state</Label>
                    <Select value={state} onValueChange={(v) => setState(v as IncidentState)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {INCIDENT_STATES.map((s) => (
                          <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Textarea
                    rows={3}
                    placeholder="What changed?"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                  <div className="flex justify-end">
                    <Button onClick={() => post.mutate()} disabled={post.isPending || message.trim().length < 1}>
                      {post.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Post update
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Timeline
              </div>
              <div className="space-y-3">
                {updates.map((u: any) => (
                  <div key={u.id} className="border-l-2 pl-3 py-1">
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <Badge variant="outline" className={stateTone(u.state)}>
                        <span className="inline-flex items-center gap-1">
                          <StateIcon state={u.state} /> {u.state}
                        </span>
                      </Badge>
                      <span>{format(new Date(u.created_at), "MMM d, HH:mm")}</span>
                      <span>· {formatDistanceToNow(new Date(u.created_at), { addSuffix: true })}</span>
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap">{u.message}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StatCard({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number | string;
  tone?: "default" | "warn" | "alert";
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div
          className={
            "text-2xl font-semibold mt-1 " +
            (tone === "alert" && Number(value) > 0
              ? "text-destructive"
              : tone === "warn" && Number(value) > 0
                ? "text-warning"
                : "")
          }
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}